import { notificationStillVisible, type NotificationFeed, type NotificationQuery, type SiteNotification } from '@revival/contracts';
import { api } from './api.js';
import { cachedPaths, cachedValue, cacheValue, disconnected, forgetCached, isNetworkFailure } from './offline-cache.js';

const receiptPrefix = '/notifications/read/';
export const notificationFeedPath = (query: NotificationQuery) => `/notifications/feed/${query.unread ? 'unread' : 'all'}/${query.cursor ?? 'first'}`;
const sameIdentity = (owner: string, identity: number) => api.sessionIdentity() === identity && (api.isViewingAs() ? owner === api.workOwner('') : owner === api.currentOwner());
export async function flushNotificationReads(owner: string) {
  if (disconnected()) return;
  const identity = api.sessionIdentity(), paths = await cachedPaths(owner, receiptPrefix);
  for (let offset = 0; offset < paths.length; offset += 100) {
    if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
    const batch = paths.slice(offset, offset + 100);
    const timestamps = await Promise.all(batch.map(path => cachedValue<string>(owner, path)));
    const groups = new Map<string | undefined, string[]>();
    batch.forEach((path, index) => {
      const timestamp = timestamps[index];
      const readAt = timestamp && Number.isFinite(Date.parse(timestamp)) ? new Date(timestamp).toISOString() : undefined;
      groups.set(readAt, [...(groups.get(readAt) ?? []), path]);
    });
    for (const [readAt, paths] of groups) {
      if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
      await api.readNotifications(paths.map(path => path.slice(receiptPrefix.length)), readAt);
      if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
      await Promise.all(paths.map(path => forgetCached(owner, path)));
    }
  }
}
export async function loadNotificationFeed(owner: string, query: NotificationQuery) {
  const identity = api.sessionIdentity(), path = notificationFeedPath(query);
  try {
    await flushNotificationReads(owner);
    if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
    const feed = await api.notifications(query);
    if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
    if (!api.isViewingAs()) await cacheValue(owner, path, feed);
    return { feed: { ...feed, items: feed.items.filter(item => notificationStillVisible(item.readAt)) }, saved: false };
  } catch (error) {
    if (!sameIdentity(owner, identity)) throw error;
    if (!isNetworkFailure(error) || api.isViewingAs()) { if (!isNetworkFailure(error)) await forgetCached(owner, path); throw error; }
    const feed = await cachedValue<NotificationFeed>(owner, path);
    if (!feed) throw new Error('Connect once to load your notifications on this device.');
    const paths = await cachedPaths(owner, receiptPrefix);
    const timestamps = await Promise.all(paths.map(path => cachedValue<string>(owner, path)));
    const pending = new Map(paths.map((path, index) => [path.slice(receiptPrefix.length), timestamps[index]]));
    const items = feed.items.map(item => pending.has(item.id) ? { ...item, readAt: item.readAt ?? pending.get(item.id) ?? new Date().toISOString() } : item)
      .filter(item => notificationStillVisible(item.readAt));
    return { feed: { ...feed, items: query.unread ? items.filter(item => !item.readAt) : items, unreadCount: Math.max(0, feed.unreadCount - [...pending.keys()].filter(id => !feed.items.some(item => item.id === id && item.readAt)).length) }, saved: true };
  }
}
export async function markNotificationRead(owner: string, item: SiteNotification) {
  const identity = api.sessionIdentity();
  if (!sameIdentity(owner, identity)) throw new Error('The signed-in account changed.');
  try {
    const { readIds } = await api.readNotifications([item.id]);
    if (!readIds.includes(item.id)) throw new Error('This notification is no longer available. Refresh the list.');
  } catch (error) {
    if (!sameIdentity(owner, identity) || !isNetworkFailure(error) || api.isViewingAs()) throw error;
    // Separate keys keep simultaneous reads in two tabs from overwriting each other.
    if (!await cachedValue<string>(owner, `${receiptPrefix}${item.id}`) && !await cacheValue(owner, `${receiptPrefix}${item.id}`, new Date().toISOString())) throw new Error('This browser could not save your read status. Reconnect and try again.');
  }
}
